/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { NativeInput, Select, Text } from '@/shared/ui';
import { formatPhoneDisplay } from '@/shared/utils/format-phone';
import { OTHER_PHONE, type OlxPhoneOption } from './olx-phone';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Select entries: broker (default), agency, then "Outro número". */
export function olxPhoneSelectData(options: OlxPhoneOption[], t: Translate): { value: string; label: string }[] {
  return [
    ...options.map((o) => ({
      value: o.phone,
      label: o.source === 'broker'
        ? t('publish.olxPhone.broker', { phone: formatPhoneDisplay(o.phone), name: o.label })
        : t('publish.olxPhone.agency', { phone: formatPhoneDisplay(o.phone) }),
    })),
    { value: OTHER_PHONE, label: t('publish.olxPhone.other') },
  ];
}

/** OLX statuses meaning the property already has its ad there. */
const OLX_AD_EXISTS = new Set(['published', 'queued']);

/** Whether the property's OLX ad already exists (so publishing updates it). */
export function hasOlxAd(savedStatus: Record<string, string>): boolean {
  return OLX_AD_EXISTS.has(savedStatus.olx ?? '');
}

/**
 * Each property has one OLX ad per deal (venda / aluguel): publishing again
 * updates that ad, it never creates another one (OLX refuses duplicates).
 */
export function OlxSingleAdNote({ t, existing }: { t: Translate; existing: boolean }) {
  return (
    <Text
      className="m-0 text-xs leading-relaxed text-[var(--text-secondary)] p-(--space-2) rounded-(--radius-md) bg-[var(--surface-secondary)]"
      data-testid="olx-single-ad-note"
    >
      {existing ? t('publish.olxSingleAd.update') : t('publish.olxSingleAd.new')}
    </Text>
  );
}

/**
 * "Telefone no anúncio OLX": shown while OLX is selected. OLX requires a
 * phone on every ad, so publishing waits for a valid one.
 */
export function OlxPhonePanel({ t, options, loading, choice, setChoice, custom, setCustom, phone }: {
  t: Translate;
  options: OlxPhoneOption[];
  loading: boolean;
  choice: string;
  setChoice: (value: string) => void;
  custom: string;
  setCustom: (value: string) => void;
  phone: string | null;
}) {
  const typedInvalid = choice === OTHER_PHONE && custom.trim() !== '' && !phone;
  return (
    <div className="space-y-(--space-1-5)" data-testid="olx-phone-panel">
      <Text as="span" className="text-xs font-semibold text-[var(--text-primary)]">{t('publish.olxPhone.title')}</Text>
      {loading ? (
        <Text className="m-0 text-xs text-[var(--text-tertiary)]">{t('publish.olxPhone.loading')}</Text>
      ) : (
        <>
          <Select
            name="olx-phone"
            data-testid="olx-phone-select"
            aria-label={t('publish.olxPhone.title')}
            value={choice}
            onChange={(value) => setChoice(String(value ?? OTHER_PHONE))}
            data={olxPhoneSelectData(options, t)}
          />
          {choice === OTHER_PHONE && (
            <NativeInput
              type="tel"
              inputMode="tel"
              value={custom}
              placeholder="(11) 98765-4321"
              onChange={(e) => setCustom(e.target.value)}
              aria-label={t('publish.olxPhone.other')}
              data-testid="olx-phone-input"
              className="w-full px-(--space-2) py-(--space-1) text-xs rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] text-[var(--text-primary)]"
            />
          )}
          {typedInvalid && (
            <Text className="m-0 text-xs text-[var(--status-error-text)]" data-testid="olx-phone-invalid">
              {t('publish.olxPhone.invalid')}
            </Text>
          )}
          <Text className="m-0 text-xs text-[var(--text-tertiary)]">{t('publish.olxPhone.hint')}</Text>
        </>
      )}
    </div>
  );
}
